import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Mail, RefreshCw, Send, X, ArrowLeft, MailOpen } from 'lucide-react';
import { useState, useEffect } from 'react';
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailReaderSection = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isReplying, setIsReplying] = useState(false);
  const [replyBody, setReplyBody] = useState('');

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/email/inbox`);
      setEmails(response.data.messages || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Failed to load emails');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails();
    // Refresh every 5 minutes
    const interval = setInterval(fetchEmails, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleReply = async () => {
    if (!replyBody.trim()) {
      toast.error('Please enter a message');
      return;
    }

    try {
      await axios.post(`${API}/email/send`, null, {
        params: {
          to: selectedEmail.from_address,
          subject: `Re: ${selectedEmail.subject}`,
          body: replyBody,
          in_reply_to: selectedEmail.id
        }
      });

      toast.success('Reply sent successfully');
      setIsReplying(false);
      setReplyBody('');
      setSelectedEmail(null);
      fetchEmails();
    } catch (error) {
      console.error('Error sending reply:', error);
      toast.error('Failed to send reply');
    }
  };

  const unreadCount = emails.filter(e => e.is_unread).length;

  return (
    <div className="content-card content-card-large" data-testid="email-reader-section">
      <div className="card-header">
        <h2>
          <Mail size={20} className="inline" /> Studio Email
          {unreadCount > 0 && (
            <span className="unread-badge">{unreadCount}</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {selectedEmail && (
            <Button 
              onClick={() => { setSelectedEmail(null); setIsReplying(false); }} 
              variant="outline" 
              size="sm"
            >
              <ArrowLeft size={16} /> Back
            </Button>
          )}
          <Button 
            onClick={fetchEmails} 
            variant="outline" 
            size="sm"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </Button>
        </div>
      </div>

      <ScrollArea className="card-content">
        {loading && !selectedEmail ? (
          <p className="loading-text">Loading emails...</p>
        ) : selectedEmail ? (
          <div className="email-view">
            <div className="email-header">
              <div className="email-meta">
                <strong>From:</strong> {selectedEmail.from_address}
              </div>
              <div className="email-meta">
                <strong>Subject:</strong> {selectedEmail.subject}
              </div>
              <div className="email-meta">
                <strong>Date:</strong> {selectedEmail.date}
              </div>
            </div>

            <div className="email-body">
              <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                {selectedEmail.body}
              </pre>
            </div>

            {!isReplying ? (
              <Button onClick={() => setIsReplying(true)} className="reply-btn">
                <Send size={16} /> Reply
              </Button>
            ) : (
              <div className="reply-form">
                <textarea
                  value={replyBody}
                  onChange={(e) => setReplyBody(e.target.value)}
                  className="reply-textarea"
                  placeholder="Type your reply..."
                  rows={8}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#000000'
                  }}
                />
                <div className="reply-actions">
                  <Button onClick={handleReply} className="send-btn">
                    <Send size={16} /> Send Reply
                  </Button>
                  <Button onClick={() => { setIsReplying(false); setReplyBody(''); }} variant="outline">
                    <X size={16} /> Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="email-list">
            {emails.length === 0 ? (
              <p className="no-data">No emails found</p>
            ) : (
              emails.map((email, index) => (
                <div
                  key={email.id || index}
                  className={`email-item ${email.is_unread ? 'unread' : ''}`}
                  onClick={() => setSelectedEmail(email)}
                  style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : 'rgba(255, 193, 7, 0.4)',
                    border: index % 2 === 0 ? '2px solid #e0e0e0' : '2px solid #FFC107',
                    marginBottom: '1rem',
                    padding: '1.5rem'
                  }}
                >
                  <div className="email-item-header">
                    {email.is_unread && <MailOpen size={16} className="unread-icon" />}
                    <strong className="email-from" style={{ color: '#000000' }}>{email.from_address}</strong>
                    <span className="email-date" style={{ color: '#666666' }}>{new Date(email.date).toLocaleString()}</span>
                  </div>
                  <div className="email-subject" style={{ color: '#000000', fontWeight: 'bold' }}>{email.subject}</div>
                  <div className="email-preview" style={{ color: '#333333' }}>{email.preview}</div>
                </div>
              ))
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};

export default EmailReaderSection;
